// src/components/NajlepszeStrzelnice.jsx
// Ranking ogólnopolski. Świadomie NIE jest to ranking jakości obsługi -
// nie mamy opinii użytkowników i nie zamierzamy ich udawać (ta sama zasada,
// dla której serwis nie publikuje schematu Review/AggregateRating).
import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../css/modules/Page.module.css';
import list from '../css/modules/Strzelnice.module.css';
import Faq from './Faq';
import JsonLd from '../seo/JsonLd';
import { OpinieProvider } from './Opinie';
import StrzelnicaKarta from './StrzelnicaKarta';
import { OBIEKTY, RANKING_PL, KRYTERIA, MIASTA, STRZELNICE_STATS } from '../data/strzelnice';
import { STRZELNICE_COPY } from '../seo/strzelniceCopy';
import { breadcrumbJsonLd, faqJsonLd } from '../seo/siteMeta';
import { trackEvent } from '../lib/analytics';
import { zLiczba, OBIEKTY_FORMS } from '../lib/odmiana';

const PUNKTY = {
  tel: 3,
  www: 3,
  godz: 2,
  adres: 2,
  kryta: 2,
  mail: 1,
  klub: 1,
  opis: 1,
};

const FAQ_ITEMS = [
  {
    question: 'Na jakiej podstawie powstał ten ranking strzelnic?',
    answer:
      'Każdy obiekt dostaje punkty za informacje, które sam publikuje i które udało nam się potwierdzić: numer telefonu, własna strona internetowa, godziny otwarcia, pełny adres z ulicą, kontakt e-mail, opis oferty, a także za to, czy jest strzelnicą krytą (czynną cały rok) i czy działa przy klubie strzeleckim. Suma punktów wyznacza kolejność.',
  },
  {
    question: 'Czy wysoka pozycja oznacza, że strzelnica jest lepsza?',
    answer:
      'Nie. Oznacza, że obiekt łatwo sprawdzić i umówić: ma telefon, stronę, podane godziny i konkretny adres. Nie oceniamy jakości instruktorów, stanu broni, atmosfery ani cen - nie mamy do tego wiarygodnych danych. Świetna strzelnica klubowa, która nie prowadzi strony internetowej, wypadnie w tym zestawieniu nisko.',
  },
  {
    question: 'Dlaczego nie ma tu ocen i gwiazdek?',
    answer:
      'Bo musielibyśmy je wymyślić. Nie zbieramy opinii użytkowników o strzelnicach, więc publikowanie ocen albo danych strukturalnych z gwiazdkami byłoby wprowadzaniem w błąd czytelników i wyszukiwarki. Wolimy jawne, sprawdzalne kryterium niż efektowną, ale nieprawdziwą punktację.',
  },
  {
    question: 'Jak wybrać strzelnicę pod patent strzelecki?',
    answer:
      'Szukaj obiektu prowadzonego przez klub zrzeszony w PZSS - to klub potwierdza treningi, kieruje na egzamin i prowadzi licencję zawodniczą. W zestawieniu takie obiekty mają kryterium „klub strzelecki”. Sam egzamin praktyczny odbywa się na strzelnicy, a część teoretyczną zdajesz przed komisją po nauce w domu.',
  },
];

const NajlepszeStrzelnice = () => {
  const copy = STRZELNICE_COPY.ranking || {};
  const items = RANKING_PL.map(i => OBIEKTY[i]);
  const { total, listed } = STRZELNICE_STATS;

  return (
    <OpinieProvider>
      <JsonLd
        data={[
          breadcrumbJsonLd([
            { name: 'Strona główna', path: '/' },
            { name: 'Strzelnice w Polsce', path: '/strzelnice' },
            { name: 'Najlepsze strzelnice w Polsce', path: '/najlepsze-strzelnice-w-polsce' },
          ]),
          faqJsonLd(FAQ_ITEMS),
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Najlepiej udokumentowane strzelnice w Polsce',
            numberOfItems: items.length,
            itemListElement: items.map((item, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: `${item.n} (${item.c})`,
            })),
          },
        ]}
      />

      <main className={styles.page}>
        <article className={styles.wrap}>
          <nav className={styles.breadcrumbs} aria-label="Ścieżka nawigacji">
            <Link to="/">Strona główna</Link> › <Link to="/strzelnice">Strzelnice</Link> ›{' '}
            <span>Najlepsze strzelnice w Polsce</span>
          </nav>

          <h1 className={styles.h1}>
            Najlepsze strzelnice w Polsce - ranking {items.length} obiektów (2026)
          </h1>

          <p className={styles.lead}>
            {copy.lead || (
              <>
                „Najlepsze” znaczy tu: <strong>najlepiej udokumentowane i najłatwiej dostępne</strong>.
                Zestawienie porządkuje {zLiczba(listed, OBIEKTY_FORMS)} z potwierdzonymi danymi według tego, ile
                sprawdzalnych informacji publikują i czy są czynne cały rok - a nie według ocen,
                których nie zbieramy.
              </>
            )}
          </p>

          <p className={styles.updated}>
            Aktualizacja: lipiec 2026 · baza {total} obiektów · dane z OpenStreetMap (ODbL)
            i ze stron samych strzelnic
          </p>

          {copy.intro && (
            <section className={styles.section}>
              {copy.intro.map(paragraph => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </section>
          )}

          <section className={styles.section} id="metodologia">
            <h2>Jak liczymy punkty - i czego ta metoda nie mierzy</h2>
            <table className={list.scoreTable}>
              <caption>Punktacja przyznawana obiektowi</caption>
              <thead>
                <tr>
                  <th scope="col">Kryterium</th>
                  <th scope="col">Punkty</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(PUNKTY).map(([code, points]) => (
                  <tr key={code}>
                    <td>{KRYTERIA[code]}</td>
                    <td>{points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {copy.metodologia ? (
              copy.metodologia.map(paragraph => <p key={paragraph.slice(0, 40)}>{paragraph}</p>)
            ) : (
              <p>
                Wszystkie kryteria da się sprawdzić bez naszej opinii: albo obiekt podaje telefon,
                albo nie. Dzięki temu kolejność jest powtarzalna i możesz ją zweryfikować sam.
              </p>
            )}
            <p className={styles.note}>
              Czego ta metoda <strong>nie</strong> mierzy: jakości instruktorów, stanu broni,
              atmosfery, poziomu cen ani bezpieczeństwa obiektu. Wysoka pozycja to obiekt, który
              łatwo sprawdzić i umówić. Niska pozycja najczęściej oznacza strzelnicę klubową, która
              po prostu nie prowadzi strony internetowej - a bywa świetna.
            </p>
          </section>

          <section className={styles.section} id="ranking">
            <h2>Ranking - {items.length} najlepiej udokumentowanych strzelnic</h2>
            <ol className={list.cards}>
              {items.map((item, i) => (
                <StrzelnicaKarta
                  item={item}
                  position={i + 1}
                  showPlace
                  showCriteria
                  key={`${item.n}-${item.c}`}
                />
              ))}
            </ol>
          </section>

          <div className={styles.ctaBox}>
            <h2>Na strzelnicę pójdziesz i tak - egzamin zdaje się z teorii</h2>
            <p>
              Część praktyczna egzaminu na patent strzelecki odbywa się na strzelnicy, ale to test
              wiedzy najczęściej przesądza o wyniku. Sprawdź się na prawdziwych pytaniach.
            </p>
            <div className={styles.ctaRow}>
              <Link
                to="/intro"
                className={styles.primaryButton}
                onClick={() => trackEvent('cta_click', { location: 'ranking', label: 'demo' })}
              >
                Rozwiąż darmowy test
              </Link>
              <Link to="/cennik" className={styles.secondaryButton}>
                Zobacz cennik
              </Link>
            </div>
          </div>

          <section className={styles.section}>
            <h2>Ranking strzelnic w największych miastach</h2>
            <ul className={styles.linkList}>
              {MIASTA.map(c => (
                <li key={c.slug}>
                  <Link to={`/strzelnice/miasto/${c.slug}`}>
                    Najlepsze strzelnice - {c.name} ({zLiczba(c.count, OBIEKTY_FORMS)})
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/strzelnice">Pełny katalog strzelnic w Polsce ({zLiczba(total, OBIEKTY_FORMS)})</Link>
              </li>
            </ul>
          </section>

          <section className={styles.section} id="faq">
            <h2>Ranking strzelnic - pytania i odpowiedzi</h2>
            <Faq items={FAQ_ITEMS} />
          </section>
        </article>
      </main>
    </OpinieProvider>
  );
};

export default NajlepszeStrzelnice;
